import { Add } from "iconsax-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SubscribePatientModal } from "./components/SubscribePatientModal";
import { CMSSheetContent } from "./components/SheetContent";
import { DeleteConfirmModal } from "../../../cms/components/DeleteConfirmModal";
import { DateFilterModal } from "../../../cms/components/DateFilterModal";
import { CMSFilterBar } from "../../../cms/components/CMSFilterBar";
import { CMSTable } from "../../../cms/components/CMSTable";
import { CMSPagination } from "../../../cms/components/CMSPagination";
import { CMSSheet } from "../../../cms/components/CMSSheet";
import instance from "../../../../../axios/instance";

export default function PrescriptionPage() {
  const [prescriptions, setPrescriptions] = useState([]);
  const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
  const [paginatedPrescriptions, setPaginatedPrescriptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const itemsPerPage = 10;
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [triggerEditModal, setTriggerEditModal] = useState(0);
  const [editHandlers, setEditHandlers] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    product: null,
  });
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [activeTab, setActiveTab] = useState("patientDetails");
  const [initialTab, setInitialTab] = useState("patientDetails");
  const [treatmentView, setTreatmentView] = useState("list");
  const [formData, setFormData] = useState({
    name: "",
    treatment: "",
    price: "",
  });
  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  const fetchPrescriptions = async () => {
    setLoading(true);
    try {
      const response = await instance.get("/doctor/prescriptions/all", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const prescriptionsData = Array.isArray(response.data) ? response.data : [];
      const patientMap = new Map();

      prescriptionsData.forEach((item) => {
        const patientKey = item.patientId || item.email;
        if (!patientMap.has(patientKey)) {
          patientMap.set(patientKey, {
            ...item,
            treatmentsList: [],
            subscriptionIds: [],
          });
        }
        const patient = patientMap.get(patientKey);
        const treatmentName = item.treatment?.name || item.medication;
        
        const existingTreatment = patient.treatmentsList.find(t => t.name === treatmentName);
        
        if (!existingTreatment) {
          patient.treatmentsList.push({
            name: treatmentName,
            price: item.treatment?.price || 0,
            subscriptionId: item.id,
            status: item.status || "pending",
            updated: item.updatedAt || item.createdAt,
          });
        }
        patient.subscriptionIds.push(item.id);
      });

      const transformedData = Array.from(patientMap.values()).map((item) => ({
        id: item.id,
        name: item.fullName || `${item.firstName} ${item.lastName}`,
        email: item.email,
        treatments: item.treatmentsList.length,
        treatmentsList: item.treatmentsList,
        medication: item.medication,
        price: item.treatment?.price || 0,
        status: item.status || "pending",
        progress: {
          questionnaire: item.questionnaireCompleted || false,
          registration: item.accountCreated || false,
          payment: item.paymentCompleted || false,
        },
        questionnaireId: item.questionnaireId,
        meetingId: item.meetingId,
        patientId: item.patientId,
        subscriptionIds: item.subscriptionIds,
        updated: item.updatedAt || item.createdAt,
        rawData: item,
      }));

      setPrescriptions(transformedData);
      setFilteredPrescriptions(transformedData);
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
      const status = error.response?.status;
      if (status && ![404, 403, 401].includes(status)) {
        toast.error("Failed to load prescriptions. Please try again.", {
          toastId: "fetch-prescriptions-error",
        });
      }
      setPrescriptions([]);
      setFilteredPrescriptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  useEffect(() => {
    let filtered = prescriptions;

    if (searchQuery) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.category.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (selectedStatus !== "All Status") {
      filtered = filtered.filter(
        (product) => product.status === selectedStatus,
      );
    }

    if (dateRange.startDate && dateRange.endDate) {
      filtered = filtered.filter((product) => {
        const productDate = new Date(product.updated);
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        return productDate >= start && productDate <= end;
      });
    }

    setFilteredPrescriptions(filtered);
    setPageIndex(1);
  }, [searchQuery, selectedStatus, dateRange, prescriptions]);

  useEffect(() => {
    const startIndex = (pageIndex - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedPrescriptions(
      filteredPrescriptions.slice(startIndex, endIndex),
    );
  }, [filteredPrescriptions, pageIndex]);

  const handleAction = (action, product) => {
    setOpenDropdown(null);

    switch (action) {
      case "edit-personal":
        setSelectedRow(product);
        setIsSheetOpen(true);
        setTimeout(() => {
          setTriggerEditModal((prev) => prev + 1);
        }, 300);
        break;
      case "add-treatment":
        setSelectedRow(product);
        setInitialTab("treatments");
        setIsSheetOpen(true);
        break;
      case "edit-questionnaire":
        setSelectedRow(product);
        setInitialTab("questionnaires");
        setIsSheetOpen(true);
        setTimeout(() => {
          setTriggerEditModal((prev) => prev + 1);
        }, 300);
        break;
      default:
        break;
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setIsSheetOpen(true);
  };

  const handleEditProduct = (product) => {
    setIsEditing(true);
    setEditingId(product.id);
    setFormData({
      name: product.name || "",
      treatment: product.treatment || "",
      price: product.price,
    });
    setIsSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setIsEditing(false);
    setEditingId(null);
    setInitialTab("patientDetails");
  };

  const handleSaveTreatment = () => {
    setLoading(true);

    setTimeout(() => {
      if (isEditing) {
        setPrescriptions((prev) =>
          prev.map((t) =>
            t.id === editingId
              ? {
                  ...t,
                  name: formData.name,
                  treatment: formData.treatment,
                  price: parseFloat(formData.price),
                  updated: new Date().toISOString().split("T")[0],
                }
              : t,
          ),
        );
        toast.success("Treatment updated successfully!");
      } else {
        const newTreatment = {
          id: prescriptions.length + 1,
          name: formData.name,
          treatment: formData.treatment,
          price: parseFloat(formData.price),
          status: "draft",
          updated: new Date().toISOString().split("T")[0],
        };

        setPrescriptions([newTreatment, ...prescriptions]);
        toast.success("Treatment added successfully!");
      }

      setLoading(false);
      setIsSheetOpen(false);
      setIsEditing(false);
      setEditingId(null);
    }, 1000);
  };

  const handleDeleteConfirm = () => {
    setLoading(true);

    setTimeout(() => {
      setPrescriptions((prev) =>
        prev.filter((t) => t.id !== deleteModal.product.id),
      );
      toast.success("Prescription deleted successfully!");
      setLoading(false);
      setDeleteModal({ isOpen: false, product: null });
    }, 500);
  };

  const isFormValid =
    formData.name && formData.treatment && formData.category && formData.price;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const columns = [
    { key: "name", label: "PATIENT NAME", align: "left" },
    { key: "treatments", label: "Treatments", align: "left" },
    { key: "price", label: "PRICE", align: "left" },
    { key: "status", label: "Status", align: "left" },
    { key: "updated", label: "Updated", align: "left" },
    { key: "actions", label: "", align: "center" },
  ];

  const actions = [
    {
      key: "edit-personal",
      label: "Edit Personal Info",
      variant: "primary",
    },
    {
      key: "add-treatment",
      label: "Add Treatment",
      variant: "primary",
    },
    {
      key: "edit-questionnaire",
      label: "Edit Questionnaire",
      variant: "primary",
    },
  ];

  const renderCell = (key, item) => {
    switch (key) {
      case "name":
        return (
          <div className="text-[14px] font-medium text-gray-900">
            {item.name}
          </div>
        );
      case "treatments":
        return (
          <div className="text-[14px] text-gray-600">{item.treatments}</div>
        );
      case "price":
        return (
          <div className="text-[14px] font-medium text-gray-900">
            ${item.price.toFixed(2)}/mo
          </div>
        );
      case "status":
        return (
          <span
            className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium capitalize ${
              item.status === "completed"
                ? "bg-green-100 text-green-800"
                : item.status === "active"
                  ? "bg-blue-100 text-blue-800"
                  : item.status === "pending"
                    ? "bg-yellow-100 text-yellow-800"
                    : item.status === "cancelled"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
            }`}
          >
            {item.status}
          </span>
        );
      case "updated":
        return (
          <div className="text-[14px] text-gray-600">
            {formatDate(item.updated)}
          </div>
        );
      default:
        return <div className="text-[14px] text-gray-900">{item[key]}</div>;
    }
  };
  return (
    <div className="bg-[#fafafa] min-h-screen">
      <div className="max-w-[100%] w-full px-4 lg:px-7 py-5">
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-2">
            <div className="mb-2 lg:mb-0">
              <h1 className="text-2xl font-bold text-gray-900">
                Prescriptions Order
              </h1>
              <p className="text-gray-600 text-[14px] mt-1 max-w-2xl">
                Manage all Prescription pages on your website. Click any item to
                edit details, update content, or publish new Prescriptions.{" "}
              </p>
            </div>
            <button
              onClick={handleOpenModal}
              className="bg-primary py-2.5 px-4 rounded-md w-full max-w-fit font-medium text-[14px] hover:opacity-80 text-white flex items-center gap-2 transition-all duration-300 shadow-sm"
            >
              <Add size={18} />
              Subscribe user
            </button>
          </div>
        </div>

        <CMSFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search patient name..."
          statuses={statuses}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          showDateFilter={true}
          onDateClick={() => setIsDateFilterOpen(true)}
        />

        <CMSTable
          columns={columns}
          data={paginatedPrescriptions}
          loading={loading}
          emptyMessage="No prescriptions found"
          onActionClick={handleAction}
          onRowClick={handleRowClick}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          actions={actions}
          renderCell={renderCell}
        />

        {!loading && (
          <CMSPagination
            currentPage={pageIndex}
            totalItems={filteredPrescriptions.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setPageIndex}
            itemName="prescription"
            showingCount={paginatedPrescriptions.length}
          />
        )}
      </div>

      <SubscribePatientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false);
          fetchPrescriptions();
        }}
      />

      <CMSSheet
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        onCancel={() => {
          if (editHandlers?.type === "questionnaire") {
            editHandlers.handlers?.onCancel();
          } else {
            handleCloseSheet();
          }
        }}
        title="Prescription Details"
        saveButtonText={
          editHandlers?.type === "questionnaire"
            ? "Save Changes"
            : activeTab === "treatments" && treatmentView === "details"
              ? "Add Record"
              : activeTab === "treatments"
                ? "Change Treatment"
                : "Edit Details"
        }
        cancelButtonText={
          editHandlers?.type === "questionnaire" ? "Cancel" : "Close"
        }
        isLoading={editHandlers?.handlers?.loading || loading}
        onSave={() => {
          if (editHandlers?.type === "questionnaire") {
            editHandlers.handlers?.onSave();
          } else if (activeTab === "treatments") {
            setTriggerEditModal((prev) => prev + 1);
          } else {
            setTriggerEditModal((prev) => prev + 1);
          }
        }}
      >
        <CMSSheetContent
          selectedRow={selectedRow}
          onRefresh={fetchPrescriptions}
          triggerEditModal={triggerEditModal}
          onGetEditHandlers={setEditHandlers}
          onTabChange={setActiveTab}
          onTreatmentViewChange={setTreatmentView}
          initialTab={initialTab}
        />
      </CMSSheet>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, treatment: null })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.treatment?.name}
        isLoading={loading}
      />

      <DateFilterModal
        isOpen={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        onApply={() => {}}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />
    </div>
  );
}

const dummyPrescriptions = [
  {
    id: 1,
    name: "Vivian Holloway",
    treatments: 8,
    price: 299.99,
    status: "published",
    updated: "2025-01-15",
  },
  {
    id: 2,
    name: "Ezra Sterling",
    treatments: 2,
    price: 199.99,
    status: "published",
    updated: "2025-01-14",
  },
  {
    id: 3,
    name: "Celeste Moreau",
    treatments: 4,
    price: 20.99,
    status: "draft",
    updated: "2025-01-12",
  },
];

const statuses = ["All Status", "active", "pending", "completed", "inactive", "cancelled"];
